/**
 * Companies API Hooks
 * TanStack Query hooks for companies domain
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { api } from "@/lib/api/client";

export interface Company {
  id: string;
  name: string;
  cnpj?: string;
  document?: string | null;
  type?: string | null;
  address?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  phone?: string | null;
  email?: string | null;
  status?: string;
  isMaster?: boolean;
  createdAt?: string | Date;
  updatedAt?: string | Date | null;
}

export function useCompanies(
  params?: { search?: string; status?: "active" | "inactive" },
  enabled?: boolean
) {
  return useQuery({
    queryKey: ["companies", params],
    queryFn: async () => {
      const response = await api.companies.list(params);
      // ✅ FIX: Garantir que sempre retornamos um objeto válido (nunca undefined)
      return {
        data: (response?.data ?? []) as Company[],
        pagination: response?.pagination,
      };
    },
    enabled: enabled !== false, // Por padrão true, mas pode ser desabilitado
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 15 * 60 * 1000, // 15 minutos
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useCreateCompany(options?: {
  onSuccess?: (data: unknown) => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; document?: string; type?: string; address?: string; number?: string; complement?: string; neighborhood?: string; city?: string; state?: string; zipCode?: string; phone?: string; email?: string; status?: string }) => {
      const response = await api.companies.create(data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useUpdateCompany(options?: {
  onSuccess?: (data: unknown) => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Company> & { name?: string; document?: string; type?: string; address?: string; number?: string; complement?: string; neighborhood?: string; city?: string; state?: string; zipCode?: string; phone?: string; email?: string; status?: string } }) => {
      const response = await api.companies.update(id, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useDeleteCompany(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.companies.delete(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["hierarchy"] });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

export function useCurrentCompany() {
  // SOLUÇÃO SIMPLES: Retorna dados básicos da sessão
  // A empresa atual já está na sessão do NextAuth
  const { data: session, status: sessionStatus } = useSession();
  // Trata string vazia como null/undefined
  const currentCompanyId = session?.user?.currentCompanyId?.trim() || null;

  return useQuery({
    queryKey: ["companies", "current", currentCompanyId],
    queryFn: async () => {
      // Se não houver companyId, retorna null em vez de lançar erro
      // Isso evita que o componente quebre durante o carregamento inicial
      if (!currentCompanyId) {
        return null;
      }

      // Retornar dados da sessão (suficiente para o navbar)
      const company: Company = {
        id: currentCompanyId,
        name: "TopaMais", // TODO: Buscar nome real do backend quando JWT funcionar
        status: "active",
        isMaster: session?.user?.roles?.includes("super_admin") ||
                  session?.user?.roles?.includes("admin_interno") ||
                  false,
      };

      return company;
    },
    // Só executa quando a sessão estiver autenticada e tiver companyId válido
    enabled: sessionStatus === "authenticated" && !!currentCompanyId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

// Re-export deletion types from shared file
export type { DependencyInfo, DeletionCheckResponse } from "./types/deletion";
import type { DeletionCheckResponse } from "./types/deletion";

/**
 * Hook to check if a company can be permanently deleted
 */
export function useCanDeleteCompany(id: string | null) {
  return useQuery({
    queryKey: ["companies", id, "can-delete"],
    queryFn: async () => {
      if (!id) throw new Error("ID inválido");
      const response = await api.companies.canDelete(id);
      return response.data as DeletionCheckResponse;
    },
    enabled: !!id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 60 * 1000, // 1 minuto
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

/**
 * Hook to permanently delete a company (hard delete)
 */
export function useHardDeleteCompany(options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.companies.permanentDelete(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["hierarchy"] });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
